// 📁 CongesPage.js
import React, { useState, useEffect, useContext, useMemo } from "react";
import axios from "../context/AxiosInstance";
import { Table, Button, Form, Spinner, Badge } from "react-bootstrap";
import { AuthContext } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import AppBreadcrumb from "../components/AppBreadcrumb";
import Sidebar from "../components/Sidebar";
import CongeFormModal from "../components/conge/CongeFormModal";
import ConfirmDeleteModal from "../components/conge/ConfirmDeleteModal";
import RhKpiCard from "../components/common/RhKpiCard";
import RhTablePagination from "../components/common/RhTablePagination";
import { FaCalendarAlt, FaSearch, FaPlus, FaPen, FaTrash } from "react-icons/fa";
import "../styles/rh-modern-ui.css";
import "./CongesPage.css";

const MOIS_ORDER = {
  Janvier: 1, Février: 2, Fevrier: 2, Mars: 3, Avril: 4, Mai: 5, Juin: 6,
  Juillet: 7, Août: 8, Aout: 8, Septembre: 9, Octobre: 10, Novembre: 11, Décembre: 12, Decembre: 12,
};

const normalize = (str) =>
  (str || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const CongesPage = () => {
  const { token, role } = useContext(AuthContext);

  const [conges, setConges] = useState([]);
  const [search, setSearch] = useState("");
  const [moisFilter, setMoisFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);

  const [showFormModal, setShowFormModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedConge, setSelectedConge] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [detail, setDetail] = useState(null);

  const fetchConges = async () => {
    setLoading(true);
    if (role !== "RH" && role !== "Assistant" && role !== "ASSISTANT") {
      setUnauthorized(true);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get("/conges/", {
        params: { is_active: 1 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setConges(res.data.data);
    } catch (error) {
      console.error("Erreur de chargement des congés", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && role) fetchConges();
  }, [token, role]);

  const openForm = (conge = null) => {
    setSelectedConge(conge);
    setIsEditMode(!!conge);
    setShowFormModal(true);
  };
  const openDelete = (conge) => {
    setSelectedConge(conge);
    setShowDeleteModal(true);
  };

  const filteredAndSorted = useMemo(() => {
    const ns = normalize(search);
    let rows = [...conges];
    if (moisFilter) rows = rows.filter((c) => (c.mois_conge || "") === moisFilter);
    if (ns) {
      rows = rows.filter((c) =>
        normalize(`${c.nom_employe} ${c.prenom_employe} ${c.mois_conge || ""}`).includes(ns)
      );
    }
    rows.sort((a, b) => {
      const ay = parseInt(a.annee_conge, 10) || 0;
      const by = parseInt(b.annee_conge, 10) || 0;
      if (by !== ay) return by - ay;
      return (MOIS_ORDER[b.mois_conge] || 0) - (MOIS_ORDER[a.mois_conge] || 0);
    });
    return rows;
  }, [conges, search, moisFilter]);

  const kpis = useMemo(() => {
    const list = conges || [];
    const employesUniques = new Set(list.map((c) => c.id_employe)).size;
    const joursPris = list.reduce((a, c) => a + (Number(c.jours_pris_conge) || 0), 0);
    const joursAcquis = list.reduce((a, c) => a + (Number(c.jours_acquis_conge) || 0), 0);
    const annees = new Set(list.map((c) => c.annee_conge).filter(Boolean)).size;
    return { total: list.length, employesUniques, joursPris, joursAcquis, annees };
  }, [conges]);

  const currentData = filteredAndSorted.slice((currentPage - 1) * perPage, currentPage * perPage);
  const canEdit = role === "RH" || role === "Assistant" || role === "ASSISTANT";

  const loadDetail = (c) => {
    const same = conges.filter(
      (x) => x.id_employe === c.id_employe && String(x.annee_conge) === String(c.annee_conge)
    );
    const sommeAcquis = same.reduce((acc, x) => acc + (Number(x.jours_acquis_conge) || 0), 0);
    const sommePris = same.reduce((acc, x) => acc + (Number(x.jours_pris_conge) || 0), 0);
    const moisInseres = [...new Set(same.map((x) => x.mois_conge))];
    setDetail({
      employe: `${c.nom_employe} ${c.prenom_employe}`,
      annee: c.annee_conge,
      sommeAcquis,
      sommePris,
      restant: sommeAcquis - sommePris,
      moisInseres,
    });
  };

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Navbar />
        <div className="container-fluid p-4 p-lg-5 rh-page-content">
          <AppBreadcrumb items={[{ label: "Accueil", to: "/" }, { label: "Congés" }]} />

          <header className="rh-page-header">
            <h4>
              <span className="rh-page-icon"><FaCalendarAlt /></span>
              Gestion des congés
            </h4>
            <p>Suivi des jours acquis et pris, filtrage par mois et synthèse par employé.</p>
          </header>

          {loading ? (
            <div className="rh-loading-block">
              <Spinner animation="border" />
              <p className="mt-3 mb-0">Chargement en cours…</p>
            </div>
          ) : unauthorized ? (
            <div className="rh-alert-unauth">Accès refusé</div>
          ) : (
            <>
              <div className="rh-kpi-grid">
                <RhKpiCard label="Enregistrements" value={kpis.total} variant="primary" sub="Lignes de congés actives" />
                <RhKpiCard label="Employés" value={kpis.employesUniques} variant="info" sub="Ayant au moins une saisie" />
                <RhKpiCard label="Jours acquis" value={kpis.joursAcquis} variant="success" />
                <RhKpiCard label="Jours pris" value={kpis.joursPris} variant="warning" />
                <RhKpiCard label="Années couvertes" value={kpis.annees} variant="neutral" />
              </div>

              <div className="rh-toolbar">
                <div className="rh-toolbar-left">
                  <div className="rh-search-wrap">
                    <FaSearch className="rh-search-icon" />
                    <Form.Control
                      type="text"
                      placeholder="Rechercher par nom (sans accent)…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                  <Form.Select
                    value={moisFilter}
                    onChange={(e) => { setMoisFilter(e.target.value); setCurrentPage(1); }}
                    style={{ maxWidth: 180, minHeight: 42, borderRadius: 10 }}
                  >
                    <option value="">Tous les mois</option>
                    {["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"].map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </Form.Select>
                  <span className="rh-result-pill">{filteredAndSorted.length} résultat{filteredAndSorted.length !== 1 ? "s" : ""}</span>
                </div>
                <div className="rh-toolbar-actions">
                  <Button className="rh-btn-primary" onClick={() => openForm()}>
                    <FaPlus /> Ajouter un congé
                  </Button>
                </div>
              </div>

              {detail && (
                <div className="rh-table-panel mb-3 p-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0 fw-bold">Détail — {detail.employe} ({detail.annee})</h6>
                    <Badge bg="secondary">{detail.moisInseres.length} mois saisis</Badge>
                  </div>
                  <div className="rh-kpi-grid mb-2" style={{ marginBottom: 0 }}>
                    <RhKpiCard label="Acquis" value={detail.sommeAcquis} variant="success" />
                    <RhKpiCard label="Pris" value={detail.sommePris} variant="warning" />
                    <RhKpiCard label="Restants" value={detail.restant} variant="primary" />
                  </div>
                  <small className="text-muted">Mois : {detail.moisInseres.join(", ") || "—"}</small>
                </div>
              )}

              <div className="rh-table-panel">
                <div className="table-responsive">
                  <Table hover className="rh-modern-table mb-0">
                    <thead>
                      <tr>
                        <th>Nom</th>
                        <th>Prénom</th>
                        <th>Année</th>
                        <th>Mois</th>
                        <th>Jours pris</th>
                        <th>Jours acquis</th>
                        <th>Date enregistrement</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentData.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-center text-muted py-4">Aucun congé trouvé.</td>
                        </tr>
                      ) : (
                        currentData.map((c) => (
                          <tr key={c.id_conge} onClick={() => loadDetail(c)} style={{ cursor: "pointer" }}>
                            <td><strong>{c.nom_employe}</strong></td>
                            <td>{c.prenom_employe}</td>
                            <td><span className="rh-badge rh-badge-slate">{c.annee_conge}</span></td>
                            <td>{c.mois_conge}</td>
                            <td>{c.jours_pris_conge}</td>
                            <td>{c.jours_acquis_conge}</td>
                            <td>{new Date(c.date_jour_conge).toLocaleDateString()}</td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <div className="rh-row-actions">
                                {canEdit && (
                                  <>
                                    <Button size="sm" variant="outline-success" className="rh-action-btn" onClick={() => openForm(c)}>
                                      <FaPen /> Modifier
                                    </Button>
                                    {role === "RH" && (
                                      <Button size="sm" variant="outline-danger" className="rh-action-btn" onClick={() => openDelete(c)}>
                                        <FaTrash /> Supprimer
                                      </Button>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
                <RhTablePagination
                  currentPage={currentPage}
                  totalItems={filteredAndSorted.length}
                  perPage={perPage}
                  onPageChange={setCurrentPage}
                  onPerPageChange={(n) => { setPerPage(n); setCurrentPage(1); }}
                  itemLabel="congés"
                />
              </div>
            </>
          )}
        </div>

        <CongeFormModal show={showFormModal} handleClose={() => setShowFormModal(false)} isEditMode={isEditMode} selectedConge={selectedConge} token={token} refreshData={fetchConges} />
        <ConfirmDeleteModal show={showDeleteModal} handleClose={() => setShowDeleteModal(false)} conge={selectedConge} token={token} refreshData={fetchConges} />
      </div>
    </div>
  );
};

export default CongesPage;
